import { usePaginatedQuery, queryCache } from 'react-query';
import {
  ADD_NEW_APP_VEHICLE,
  ApplicationDispatch, 
  SET_LOADING,
  SET_SERVER_MESSAGE,
  SET_VEHICLES,
  UPDATE_APP_VEHICLE_PRICE,
  updateVehicleSuccessAction
} from '../store/application/types';
import { useDispatch } from 'react-redux';
import { fetchData, postData, putData, deleteData } from './fetch';
import { isNotNil } from 'ramda';
import {hasValue, TradeInVehicle, Vehicle, VehicleUpdate} from "@amsanalytics/react-shared-library";
import {CreateVehicle} from "../Interfaces/Vehicle/CreateVehicle";
export interface QueryVehicleInventory {
  stockNumber?: string,
  vinNumber?: string,
  locationId?: string,
  yearFrom?: number,
  yearTo?: number,
  make?: string,
  minMileage?: number,
  maxMileage?: number,
  model?: string,
  priceFrom?: number,
  priceTo?: number,
  pageNumber?: number,
  pageSize?: number,
}

export const useQueryVehicleInventory = (
  params: QueryVehicleInventory
) => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const fetchVehicleInventory = async () => {
    try {

      const searchParams = new URLSearchParams();
      for (let key in params) {
        searchParams.set(key, (params as any)[key]);
      }

      const url = `api/vehicle/getvehicles?${searchParams.toString().replace(/&minMileage=NaN/, '').replace(/&maxMileage=NaN/, '')}`;
      const data = await fetchData(url, dispatch);
      const total: number = data.total;
      const pageNumber: number = data.pageNumber;
      const vehicles: Vehicle[] = data.result;
      dispatch({ type: SET_VEHICLES, payload: vehicles });
      return { total, pageNumber, vehicles };
    } catch (err) {
      dispatch({
        type: SET_SERVER_MESSAGE,
        payload: { isError: true, message: err as string }
      });
      return { total: 0, pageNumber: 0, vehicles: [] };
    }
  };

  return usePaginatedQuery(
    ['VehicleInventory', params],
    fetchVehicleInventory
  );
}

export const updateVehicleInventory = async (dispatch: ApplicationDispatch) => {
  try {
    const url = 'api/vehicleinventory/updateVehiclesByStatuses';
    await fetchData(url, dispatch);
    await queryCache.refetchQueries('VehicleInventory');
    return true;
  } catch (err) {
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    return false;
  }
}

/** Saves the vehicle; returns whether it saved. */
export const updateAmsVehicleAsync = async (dispatch: ApplicationDispatch, vehicle: Vehicle, applicationId: string, isIndirectApplication: boolean): Promise<boolean> => {
  try {
    const vehicleUpdateInfo: VehicleUpdate = {
      vehicleId: vehicle.id,
      price: vehicle.carPrice,
      mileage: vehicle.mileage,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      body: vehicle.body ?? '',
      bookoutSourceId: vehicle.bookoutSourceId,
      bookoutValue: vehicle.bookoutValue,
      otherBookoutSource: vehicle.otherBookoutSource,
      acv: vehicle.acv,
      isIndirectApplication: isIndirectApplication,
      applicationId: applicationId,
      locationOfPurchaseId: vehicle.locationOfPurchaseId,
      organizationId: vehicle.organizationId
    };

    dispatch({ type: SET_LOADING, payload: true });
    const url = `api/vehicle/update`;
    const response = await postData(url, vehicleUpdateInfo);
    const saved = isNotNil(response) && !hasValue(response.errorString as string);
    if (saved) {
      // update the vehicle in the reducer.
      dispatch(updateVehicleSuccessAction(vehicle, isIndirectApplication, response.newAppStatusId));
      // show success message to user
      dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: "Vehicle Updated Successfully", isError: false } });
    } else {
      dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: response.errorString as string, isError: true } });
      dispatch({ type: SET_LOADING, payload: false });
    }
    // refetch data after state updates
    await queryCache.refetchQueries('VehicleInventory');
    dispatch({ type: SET_LOADING, payload: false });
    return saved;
  } catch (err) {
    // show error message to user
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    dispatch({ type: SET_LOADING, payload: false });
    return false;
  }
}

export const updateVehiclePrice = async (dispatch: ApplicationDispatch, vehicleUpdateInfo: { vehicleId: string, vehiclePrice: number }, isApplicationVehicle: boolean) => {
  try {
    const url = `api/vehicle/updatePrice`;
    const response = await postData(url, vehicleUpdateInfo);
    await queryCache.refetchQueries('VehicleInventory');
    if (isApplicationVehicle) {
      dispatch({ type: UPDATE_APP_VEHICLE_PRICE, payload: { vehicleUpdateInfo } });
    }
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Updated Vehicle Price', isError: false } });
    return true;
  } catch (err) {
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    return false;
  }
}

export const addNewVehicle = async (dispatch: ApplicationDispatch, vehicleToAdd: { vehicle: CreateVehicle, applicationId: string }): Promise<{wasSuccessful: boolean, errorMessage: string | undefined}> => {
  try {
    const url = `api/vehicle/addnew/${vehicleToAdd.applicationId}`;
    const response = await postData(url, vehicleToAdd.vehicle);
    dispatch({ type: ADD_NEW_APP_VEHICLE, payload: { vehicleAddInfo: response as Vehicle } });
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Added New Vehicle', isError: false } });
    return {wasSuccessful: true, errorMessage: undefined};
  } catch (err) {
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    return {wasSuccessful: false, errorMessage: err as string};
  }
}

export const validateStockNumber = async (stockNumber: string, excludeId?: string, forPosting?: boolean): Promise<{ isAvailable: boolean, conflicts: string[] }> => {
  try {
    const queryParams = new URLSearchParams();
    if (excludeId) queryParams.set('excludeProposedTradeInId', excludeId);
    if (forPosting) queryParams.set('forPosting', 'true');
    const qs = queryParams.toString();
    const response = await fetchData(`api/TradeInVehicle/validateStockNumber/${encodeURIComponent(stockNumber)}${qs ? `?${qs}` : ''}`, (() => {}) as any);
    return response as { isAvailable: boolean, conflicts: string[] };
  } catch {
    return { isAvailable: true, conflicts: [] };
  }
}

export const getDefaultTradeInStockNumber = async (vehicleStockNumber: string, slot: number = 1): Promise<string | null> => {
  try {
    const response = await fetchData(`api/TradeInVehicle/defaultStockNumber/${encodeURIComponent(vehicleStockNumber)}?slot=${slot}`, (() => {}) as any);
    return (response as { stockNumber: string | null }).stockNumber;
  } catch {
    return null;
  }
}

export interface TradeInVehicleMutationResult {
  tradeInVehicle: TradeInVehicle;
  applicationModifiedDate: string | null;
  newAppStatusId?: string;
}

export interface TradeInVehicleDeleteResult {
  deletedId: string;
  applicationModifiedDate: string | null;
}

export const addNewTradeInVehicle = async (dispatch: ApplicationDispatch, tradeInVehicle: TradeInVehicle, applicationId: string): Promise<TradeInVehicleMutationResult | undefined> => {
  try {
    const url = `api/TradeInVehicle/addNew/${applicationId}`;
    const response: TradeInVehicleMutationResult = await postData(url, tradeInVehicle);

    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Added New Trade In Vehicle', isError: false } });
    return response;
  } catch (err) {
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    return undefined;
  }
}

export const addNewTradeInVehicle2 = async (dispatch: ApplicationDispatch, tradeInVehicle: TradeInVehicle, applicationId: string): Promise<TradeInVehicleMutationResult | undefined> => {
  try {
    const url = `api/TradeInVehicle/addNew2/${applicationId}`;
    const response: TradeInVehicleMutationResult = await postData(url, tradeInVehicle);

    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Added New Trade In Vehicle 2', isError: false } });
    return response;
  } catch (err) {
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: err as string, isError: true } });
    return undefined;
  }
}

export const updateTradeInVehicle = async (dispatch: ApplicationDispatch, tradeInVehicle: TradeInVehicle): Promise<TradeInVehicleMutationResult | undefined> => {
  try {
    const url = `api/TradeInVehicle/Update`;
    const response: TradeInVehicleMutationResult = await putData(url, {
      id: tradeInVehicle.id,
      value: tradeInVehicle.tradeValue,
      payoff: tradeInVehicle.tradeBalance,
      vin: tradeInVehicle.vin,
      year: tradeInVehicle.year,
      make: tradeInVehicle.make,
      model: tradeInVehicle.model,
      color: tradeInVehicle.color,
      body: tradeInVehicle.body,
      mileage: tradeInVehicle.mileage,
      acv: tradeInVehicle.acv,
      stockNumber: tradeInVehicle.stockNumber,
      lienHolderName: tradeInVehicle.lienHolderName,
      lienHolderAddress: tradeInVehicle.lienHolderAddress,
      lienHolderCity: tradeInVehicle.lienHolderCity,
      lienHolderStateId: tradeInVehicle.lienHolderStateId,
      lienHolderZip: tradeInVehicle.lienHolderZip,
      lienHolderPhone: tradeInVehicle.lienHolderPhone,
      payoffGoodThroughDate: tradeInVehicle.payoffGoodThroughDate,
    });

    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Updated Trade In Vehicle', isError: false } });
    return response;
  } catch (err: any) {
    const errorMessage = typeof err === 'string' ? err : (err?.message ?? JSON.stringify(err));
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: errorMessage, isError: true } });
    return undefined;
  }
}

export const deleteTradeInVehicle = async (dispatch: ApplicationDispatch, tradeInVehicleId: string, applicationId: string): Promise<TradeInVehicleDeleteResult | undefined> => {
  try {
    const url = `api/TradeInVehicle/Delete/${tradeInVehicleId}?applicationId=${encodeURIComponent(applicationId)}`;
    const response: TradeInVehicleDeleteResult = await deleteData(url);

    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: 'Deleted Trade In Vehicle', isError: false } });
    return response;
  } catch (err: any) {
    const errorMessage = typeof err === 'string' ? err : (err?.message ?? JSON.stringify(err));
    dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: errorMessage, isError: true } });
    return undefined;
  }
}
